import { useCallback, useEffect, useState } from 'react';

import { Entity, stringifyEntityRef } from '@backstage/catalog-model';
import {
  discoveryApiRef,
  identityApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { ResponseError } from '@backstage/errors';
import { catalogApiRef } from '@backstage/plugin-catalog-react';

import {
  ApplicationSetStatusResponse,
  ApplicationStatusResponse,
  ChildEntityStatus,
  ProjectStatusResponse,
} from '../types';
import {
  ANNOTATION_APPLICATION_NAME,
  ANNOTATION_APPLICATION_SET_NAME,
  ANNOTATION_PROJECT_HASH,
} from '../utils';

interface DiscoveryApi {
  getBaseUrl: (pluginId: string) => Promise<string>;
}
interface IdentityApi {
  getCredentials: () => Promise<{ token?: string }>;
}

async function fetchArgocdStatus<T>(
  discoveryApi: DiscoveryApi,
  identityApi: IdentityApi,
  path: string,
  signal?: AbortSignal,
): Promise<T> {
  const baseUrl = await discoveryApi.getBaseUrl('argocd');
  const { token } = await identityApi.getCredentials();

  const headers: Record<string, string> = {};
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(`${baseUrl}${path}`, { headers, signal });

  if (!response.ok) {
    throw await ResponseError.fromResponse(response);
  }

  return response.json();
}

function useArgocdFetch<T>(path: string) {
  const discoveryApi = useApi(discoveryApiRef);
  const identityApi = useApi(identityApiRef);
  const [data, setData] = useState<T | undefined>();
  const [loading, setLoading] = useState(!!path);
  const [error, setError] = useState<Error | undefined>();

  const load = useCallback(
    async (signal?: AbortSignal) => {
      if (!path) return;
      setLoading(true);
      setError(undefined);
      try {
        const result = await fetchArgocdStatus<T>(
          discoveryApi,
          identityApi,
          path,
          signal,
        );
        if (!signal?.aborted) setData(result);
      } catch (e) {
        if (signal?.aborted) return;
        setError(e instanceof Error ? e : new Error(String(e)));
      } finally {
        if (!signal?.aborted) setLoading(false);
      }
    },
    [discoveryApi, identityApi, path],
  );

  useEffect(() => {
    if (!path) {
      setLoading(false);
      return undefined;
    }
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load, path]);

  const refresh = useCallback(() => load(), [load]);

  return { data, loading, error, refresh };
}

export const useProjectArgocdStatus = (projectName: string) =>
  useArgocdFetch<ProjectStatusResponse>(
    `/project/${encodeURIComponent(projectName)}/status`,
  );

export const useApplicationArgocdStatus = (
  appFullName: string,
  projectName: string,
) =>
  useArgocdFetch<ApplicationStatusResponse>(
    `/project/${encodeURIComponent(
      projectName,
    )}/application/${encodeURIComponent(appFullName)}/status`,
  );

export const useApplicationSetArgocdStatus = (
  appsetFullName: string,
  projectName: string,
) =>
  useArgocdFetch<ApplicationSetStatusResponse>(
    `/project/${encodeURIComponent(
      projectName,
    )}/applicationset/${encodeURIComponent(appsetFullName)}/status`,
  );

export type { ChildEntityStatus } from '../types';

export function useProjectChildEntities(projectName: string) {
  const catalogApi = useApi(catalogApiRef);
  const [apps, setApps] = useState<Entity[]>([]);
  const [appsets, setAppsets] = useState<Entity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | undefined>();

  const load = useCallback(async () => {
    setLoading(true);
    setError(undefined);
    try {
      const [appsResult, appsetsResult] = await Promise.all([
        catalogApi.getEntities({
          filter: {
            kind: 'Component',
            'spec.type': 'daas-application',
            'spec.projectName': projectName,
          },
        }),
        catalogApi.getEntities({
          filter: {
            kind: 'Component',
            'spec.type': 'daas-application-set',
            'spec.projectName': projectName,
          },
        }),
      ]);
      setApps(appsResult.items);
      setAppsets(appsetsResult.items);
    } catch (e) {
      setError(e instanceof Error ? e : new Error(String(e)));
    } finally {
      setLoading(false);
    }
  }, [catalogApi, projectName]);

  useEffect(() => {
    load();
  }, [load]);

  return { apps, appsets, loading, error, refresh: load };
}

export function useChildEntityStatuses(
  apps: Entity[],
  appsets: Entity[],
  projectName: string,
  refreshKey: number,
) {
  const discoveryApi = useApi(discoveryApiRef);
  const identityApi = useApi(identityApiRef);
  const [statuses, setStatuses] = useState<ChildEntityStatus[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const controller = new AbortController();

    async function loadAll() {
      setLoading(true);

      const appPromises = apps.map(
        async (entity): Promise<ChildEntityStatus> => {
          const appName =
            entity.metadata.annotations?.[ANNOTATION_APPLICATION_NAME] || '';
          const hash =
            entity.metadata.annotations?.[ANNOTATION_PROJECT_HASH] || '';
          const appFullName = `${appName}-${hash}`;
          const displayName =
            entity.metadata.title || appName || entity.metadata.name;
          const entityRef = stringifyEntityRef(entity);

          try {
            const data = await fetchArgocdStatus<ApplicationStatusResponse>(
              discoveryApi,
              identityApi,
              `/project/${encodeURIComponent(
                projectName,
              )}/application/${encodeURIComponent(appFullName)}/status`,
              controller.signal,
            );
            return {
              entityRef,
              displayName,
              type: 'application',
              creationStatus: data.creationStatus,
              syncStatus: data.syncStatus,
              healthStatus: data.healthStatus,
              healthStatusMessage: data.healthStatusMessage,
              lastSync: data.lastSync,
              errorMessage: data.creationStatusMessage,
              argocdUrl: data.argocdUrl,
            };
          } catch {
            return {
              entityRef,
              displayName,
              type: 'application',
              creationStatus: 'Pending',
              errorMessage: 'Unable to fetch status from ArgoCD',
            };
          }
        },
      );

      const appsetPromises = appsets.map(
        async (entity): Promise<ChildEntityStatus> => {
          const appsetName =
            entity.metadata.annotations?.[ANNOTATION_APPLICATION_SET_NAME] ||
            '';
          const hash =
            entity.metadata.annotations?.[ANNOTATION_PROJECT_HASH] || '';
          const appsetFullName = `${appsetName}-${hash}`;
          const displayName =
            entity.metadata.title || appsetName || entity.metadata.name;
          const entityRef = stringifyEntityRef(entity);

          try {
            const data = await fetchArgocdStatus<ApplicationSetStatusResponse>(
              discoveryApi,
              identityApi,
              `/project/${encodeURIComponent(
                projectName,
              )}/applicationset/${encodeURIComponent(appsetFullName)}/status`,
              controller.signal,
            );
            return {
              entityRef,
              displayName,
              type: 'applicationset',
              creationStatus: data.creationStatus,
              errorMessage: data.creationStatusMessage,
            };
          } catch {
            return {
              entityRef,
              displayName,
              type: 'applicationset',
              creationStatus: 'Pending',
              errorMessage: 'Unable to fetch status from ArgoCD',
            };
          }
        },
      );

      const allResults = await Promise.all([...appPromises, ...appsetPromises]);
      if (!controller.signal.aborted) {
        setStatuses(allResults);
        setLoading(false);
      }
    }

    if (apps.length > 0 || appsets.length > 0) {
      loadAll();
    } else {
      setStatuses([]);
      setLoading(false);
    }

    return () => controller.abort();
  }, [apps, appsets, projectName, discoveryApi, identityApi, refreshKey]);

  return { statuses, loading };
}
